import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

/**
 * Completes the password-reset flow after the user follows the link emailed
 * by `requestPasswordReset()`. Supabase Auth's `detectSessionInUrl` (enabled
 * in lib/supabaseClient.ts) exchanges the recovery token in the URL for a
 * temporary session automatically before this component's `updatePassword`
 * call runs — no token parsing happens here.
 *
 * STATUS: IMPLEMENTED_UNVERIFIED — see docs/data-dictionary.md §8k.
 */
export default function ResetPassword() {
  const { session, updatePassword } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (password.length < 8) {
      setFormError('Password minimal 8 karakter.');
      return;
    }
    if (password !== confirm) {
      setFormError('Konfirmasi password tidak cocok.');
      return;
    }
    setSubmitting(true);
    const { error } = await updatePassword(password);
    setSubmitting(false);
    if (error) {
      setFormError(error);
      return;
    }
    setDone(true);
    setTimeout(() => navigate('/app', { replace: true }), 1500);
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', fontFamily: 'var(--alex-font-family)', background: 'var(--alex-app-bg)' }}>
      <header
        style={{
          height: 'var(--alex-topbar-height)',
          background: 'var(--alex-sidebar-bg-deep)',
          display: 'flex',
          alignItems: 'center',
          padding: '0 32px',
          flexShrink: 0,
        }}
      >
        <div style={{ fontWeight: 800, fontSize: 19, letterSpacing: 0.3 }}>
          <span style={{ color: '#fff' }}>ALEX</span> <span style={{ color: 'var(--alex-primary)' }}>ERP</span>
          <span style={{ color: 'var(--alex-sidebar-text-muted)', fontWeight: 500, fontSize: 12, marginLeft: 10 }}>EL Amos Web ERP</span>
        </div>
      </header>

      <main style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div className="alex-card" style={{ width: 400, padding: 40 }}>
          <h1 style={{ fontSize: 22, fontWeight: 800, margin: '0 0 4px', color: 'var(--alex-text-primary)' }}>Atur Password Baru</h1>

          {!session ? (
            <p style={{ fontSize: 'var(--alex-fs-body)', color: 'var(--alex-text-secondary)' }}>
              Tautan reset password tidak valid atau sudah kedaluwarsa. Silakan minta tautan baru.
              <br />
              <Link to="/lupa-password" style={{ color: 'var(--alex-primary)', fontWeight: 600 }}>
                Minta tautan baru
              </Link>
            </p>
          ) : done ? (
            <p style={{ fontSize: 'var(--alex-fs-body)', color: 'var(--alex-text-secondary)' }}>Password berhasil diperbarui. Mengalihkan ke aplikasi...</p>
          ) : (
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 20 }}>
              <label style={{ fontSize: 'var(--alex-fs-caption)', color: 'var(--alex-text-secondary)', fontWeight: 600 }}>
                Password baru
                <input value={password} onChange={(e) => setPassword(e.target.value)} type="password" required autoFocus autoComplete="new-password" style={inputStyle} />
              </label>
              <label style={{ fontSize: 'var(--alex-fs-caption)', color: 'var(--alex-text-secondary)', fontWeight: 600 }}>
                Konfirmasi password baru
                <input value={confirm} onChange={(e) => setConfirm(e.target.value)} type="password" required autoComplete="new-password" style={inputStyle} />
              </label>
              <button type="submit" disabled={submitting} style={{ ...primaryButtonStyle, opacity: submitting ? 0.7 : 1, cursor: submitting ? 'default' : 'pointer' }}>
                {submitting ? 'Menyimpan...' : 'Simpan Password'}
              </button>
              {formError && (
                <p style={{ color: 'var(--alex-danger)', fontSize: 'var(--alex-fs-caption)', margin: 0 }} role="alert">
                  {formError}
                </p>
              )}
            </form>
          )}
        </div>
      </main>
    </div>
  );
}

const inputStyle = {
  display: 'block',
  width: '100%',
  padding: '10px 12px',
  marginTop: 6,
  border: '1px solid var(--alex-border)',
  borderRadius: 'var(--alex-control-radius)',
  fontSize: 'var(--alex-fs-body)',
  color: 'var(--alex-text-primary)',
  boxSizing: 'border-box',
} as const;

const primaryButtonStyle = {
  padding: '11px 12px',
  border: 'none',
  borderRadius: 'var(--alex-control-radius)',
  background: 'var(--alex-primary)',
  color: '#fff',
  fontSize: 'var(--alex-fs-body)',
  fontWeight: 700,
  cursor: 'pointer',
  width: '100%',
} as const;
